import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const INTEGRATION_TYPE = "JIRA_TEMPO";

const emptyForm = {
  name: "Jira Tempo",
  type: INTEGRATION_TYPE,
  projectId: "",
  primaryServiceUrl: "",
  primaryAuthType: "BASIC",
  primaryAuthCredential: "",
  secondaryServiceUrl: "",
  secondaryAuthType: "BEARER",
  secondaryAuthCredential: "",
  memberMappings: "[]",
  statusSyncIntervalMinutes: 15,
  fetchWorklogsDays: 30,
  active: true,
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg: #f0f2f7;
    --surface: #ffffff;
    --surface2: #f7f9fc;
    --surface3: #edf0f7;
    --border: #e1e6ef;
    --border-focus: #3b82f6;
    --accent: #2563eb;
    --accent-light: #eff6ff;
    --accent-light1: rgb(56, 24, 3);
    --accent2: #7c3aed;
    --accent2-light: #f5f3ff;
    --success: #16a34a;
    --success-light: #f0fdf4;
    --danger: #dc2626;
    --danger-light: #fef2f2;
    --warning: #d97706;
    --warning-light: #fffbeb;
    --text: #0f172a;
    --text-muted: #64748b;
    --text-dim: #94a3b8;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md: 0 4px 16px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04);
    --shadow-xl: 0 24px 56px rgba(0,0,0,0.14), 0 8px 16px rgba(0,0,0,0.08);
    --navbar-h: 90px;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; }

  /* ── FULL PAGE LAYOUT — fixed height, inner content scrolls ── */
  .ei-page {
    height: 100vh;
    background: var(--bg);
    padding-top: var(--navbar-h);
    display: flex;
    flex-direction: column;
    color: var(--text);
    overflow: hidden; /* navbar is fixed; page itself never scrolls */
  }

  /* ── MAIN CONTENT AREA — takes remaining height, scrolls vertically ── */
  .ei-content {
    flex: 1;
    min-height: 0;          /* critical for flex children to scroll */
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1.75rem 2.5rem 2.5rem;
    display: flex;
    flex-direction: column;
    width: 100%;
    /* custom scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .ei-content::-webkit-scrollbar { width: 6px; }
  .ei-content::-webkit-scrollbar-track { background: transparent; }
  .ei-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .ei-content::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

  /* ── PAGE HEADER ── */
  .ei-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;
    flex-shrink: 0;
  }
  .ei-header-left { display: flex; align-items: center; gap: .9rem; }
  .ei-header-icon {
    width: 44px; height: 44px;
    background: var(--accent-light1);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
  }
  .ei-header h4 {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0 0 .2rem;
    color: var(--text);
  }
  .ei-header p { color: var(--text-muted); margin: 0; font-size: .83rem; }

  /* Stats chips */
  .ei-stats {
    display: flex;
    gap: .85rem;
    margin-bottom: 1.25rem;
    flex-shrink: 0;
  }
  .stat-chip {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: .5rem .9rem;
    font-size: .78rem;
    font-weight: 500;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: .4rem;
    box-shadow: var(--shadow-sm);
  }
  .stat-chip strong { color: var(--text); font-size: .9rem; font-weight: 700; }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    background: var(--surface);
    color: var(--text-muted);
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: .5rem .9rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: .82rem;
    cursor: pointer;
    transition: all .15s;
    flex-shrink: 0;
  }
  .btn-back:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }

  .btn-new {
    display: inline-flex;
    align-items: center;
    gap: .5rem;
    background: var(--accent);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: .62rem 1.3rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 700;
    font-size: .86rem;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(37,99,235,.25);
    transition: all .15s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .btn-new:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(37,99,235,.32); }
  .btn-new:active { transform: none; }

  /* ── Empty state ── */
  .ei-empty {
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: 16px;
    text-align: center;
    padding: 5rem 2rem;
    color: var(--text-muted);
    box-shadow: var(--shadow-sm);
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .ei-empty-icon { font-size: 3rem; margin-bottom: 1rem; opacity: .4; }
  .ei-empty h6 { color: var(--text); font-weight: 700; margin-bottom: .4rem; font-size: 1.05rem; }
  .ei-empty p { font-size: .86rem; max-width: 340px; line-height: 1.6; }

  /* ── Full-page table card — grows to fill remaining space ── */
  .ei-table-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
    overflow: hidden;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  /* Table itself scrolls horizontally if needed, vertically within the card */
  .ei-table-scroll {
    overflow: auto;
    flex: 1;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .ei-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .ei-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .ei-table-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .ei-table-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

  .ei-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 780px;
  }
  .ei-table thead {
    background: var(--surface2);
    border-bottom: 2px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 2;
  }
  .ei-table thead th {
    padding: .9rem 1.25rem;
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--text-muted);
    text-align: left;
    white-space: nowrap;
    background: var(--surface2);
  }
  .ei-table thead th:first-child { padding-left: 1.5rem; }
  .ei-table thead th:last-child { padding-right: 1.5rem; }

  .ei-table tbody tr {
    border-bottom: 1px solid var(--border);
    transition: background .1s;
  }
  .ei-table tbody tr:last-child { border-bottom: none; }
  .ei-table tbody tr:hover { background: #f8fbff; }
  .ei-table tbody td {
    padding: 1rem 1.25rem;
    font-size: .86rem;
    color: var(--text);
    vertical-align: middle;
  }
  .ei-table tbody td:first-child { padding-left: 1.5rem; }
  .ei-table tbody td:last-child { padding-right: 1.5rem; }

  .td-name { font-weight: 700; color: var(--text); margin-bottom: .2rem; font-size: .92rem; }
  .td-type {
    font-family: 'DM Mono', monospace;
    font-size: .68rem;
    font-weight: 500;
    color: var(--accent);
    background: var(--accent-light);
    padding: .15rem .48rem;
    border-radius: 5px;
    display: inline-block;
    margin-top: .2rem;
  }
  .td-project { font-size: .84rem; color: var(--text-muted); display: flex; align-items: center; gap: .4rem; }
  .td-url-wrap { display: flex; flex-direction: column; gap: .3rem; }
  .td-url-row { display: flex; align-items: center; gap: .4rem; }
  .td-url {
    font-family: 'DM Mono', monospace;
    font-size: .76rem;
    color: var(--text-muted);
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .url-service-label {
    font-size: .63rem;
    font-weight: 700;
    letter-spacing: .06em;
    padding: .12rem .4rem;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .url-label-jira { background: var(--accent-light); color: var(--accent); }
  .url-label-tempo { background: var(--accent2-light); color: var(--accent2); }

  .member-badge {
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    font-size: .84rem;
    font-weight: 600;
    color: var(--text);
  }
  .member-badge-dot {
    width: 24px; height: 24px;
    background: var(--accent-light);
    color: var(--accent);
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: .72rem;
    font-weight: 700;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    padding: .32rem .8rem;
    border-radius: 20px;
    font-size: .74rem;
    font-weight: 700;
    white-space: nowrap;
  }
  .status-active { background: var(--success-light); color: var(--success); }
  .status-inactive { background: var(--danger-light); color: var(--danger); }
  .status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }

  .row-actions { display: flex; gap: .4rem; align-items: center; flex-wrap: nowrap; }
  .btn-action {
    border-radius: 8px;
    padding: .38rem .75rem;
    font-size: .78rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-muted);
    transition: all .13s;
    display: inline-flex;
    align-items: center;
    gap: .3rem;
    white-space: nowrap;
  }
  .btn-action:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }
  .btn-action.edit:hover { background: var(--accent-light); color: var(--accent); border-color: #bfdbfe; }
  .btn-action.toggle-on:hover { background: var(--warning-light); color: var(--warning); border-color: #fde68a; }
  .btn-action.toggle-off:hover { background: var(--success-light); color: var(--success); border-color: #bbf7d0; }
  .btn-action.del:hover { background: var(--danger-light); color: var(--danger); border-color: #fecaca; }

  /* Table footer */
  .ei-table-footer {
    padding: .75rem 1.5rem;
    border-top: 1px solid var(--border);
    background: var(--surface2);
    font-size: .78rem;
    color: var(--text-muted);
    font-weight: 500;
    flex-shrink: 0;
  }

  /* ─────────────────────── MODAL BACKDROP ─────────────────────── */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,.5);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    /* backdrop itself does NOT scroll */
    overflow: hidden;
    animation: mFadeIn .16s ease;
  }
  @keyframes mFadeIn { from { opacity: 0 } to { opacity: 1 } }

  /* ── MODAL BOX — fixed max-height, internal body scrolls ── */
  .modal-box {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 20px;
    width: 100%;
    max-width: 760px;
    /* take up to 90% of viewport height */
    max-height: 90vh;
    box-shadow: var(--shadow-xl);
    animation: mSlideUp .2s cubic-bezier(.22,.61,.36,1);
    display: flex;
    flex-direction: column;
    /* do NOT overflow: hidden here — let children handle it */
  }
  @keyframes mSlideUp {
    from { opacity:0; transform:translateY(24px) scale(0.97) }
    to   { opacity:1; transform:none }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.4rem 1.75rem;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;  /* header never scrolls away */
  }
  .modal-header-left { display: flex; align-items: center; gap: .8rem; }
  .modal-icon-wrap {
    width: 40px; height: 40px;
    background: var(--accent-light);
    border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  .modal-title { font-weight: 700; font-size: 1.05rem; color: var(--text); }
  .modal-subtitle { font-size: .76rem; color: var(--text-muted); margin-top: .1rem; }
  .modal-close {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
    border-radius: 8px;
    width: 32px; height: 32px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    font-size: 1rem;
    transition: all .13s;
    line-height: 1;
    flex-shrink: 0;
  }
  .modal-close:hover { border-color: #fca5a5; color: var(--danger); background: var(--danger-light); }

  /* ── Scrollable modal body ── */
  .modal-body {
    padding: 1.6rem 1.75rem;
    overflow-y: auto;       /* THIS is the scroll zone */
    flex: 1;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .modal-body::-webkit-scrollbar { width: 5px; }
  .modal-body::-webkit-scrollbar-track { background: transparent; }
  .modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .modal-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

  .modal-footer {
    padding: 1.1rem 1.75rem;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: .65rem;
    background: var(--surface2);
    border-radius: 0 0 20px 20px;
    flex-shrink: 0;  /* footer never scrolls away */
  }

  /* Form sections */
  .form-section { margin-bottom: 1.6rem; }
  .form-section-label {
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: .9rem;
    display: flex;
    align-items: center;
    gap: .55rem;
  }
  .form-section-label::after { content: ''; flex: 1; height: 1px; background: var(--border); }

  .form-row { display: flex; gap: .8rem; flex-wrap: wrap; margin-bottom: .8rem; }
  .form-row:last-child { margin-bottom: 0; }
  .fg { display: flex; flex-direction: column; gap: .38rem; flex: 1; min-width: 160px; }
  .fg.full { flex: 0 0 100%; min-width: 0; }
  .fg.half { flex: 0 0 calc(50% - .4rem); min-width: 0; }
  .fg.third { flex: 0 0 calc(33.33% - .55rem); min-width: 0; }
  .fg.two-third { flex: 0 0 calc(66.66% - .275rem); min-width: 0; }

  .form-label { font-size: .78rem; font-weight: 600; color: var(--text); }
  .form-label .req { color: var(--danger); margin-left: 2px; }
  .form-hint { font-size: .71rem; color: var(--text-muted); }

  .f-control, .f-select {
    background: var(--surface) !important;
    border: 1px solid var(--border) !important;
    border-radius: 9px !important;
    color: var(--text) !important;
    font-family: 'DM Sans', sans-serif;
    font-size: .86rem;
    padding: .58rem .85rem;
    transition: border-color .14s, box-shadow .14s;
    width: 100%;
    outline: none;
  }
  .f-control:focus, .f-select:focus {
    border-color: var(--border-focus) !important;
    box-shadow: 0 0 0 3px rgba(59,130,246,.12) !important;
  }
  .f-control::placeholder { color: var(--text-dim); }

  .toggle-row { display: flex; align-items: center; gap: .7rem; padding: .5rem 0; }
  .toggle-sw { position: relative; width: 40px; height: 22px; flex-shrink: 0; }
  .toggle-sw input { opacity: 0; width: 0; height: 0; }
  .toggle-sl {
    position: absolute; inset: 0;
    background: #cbd5e1;
    border-radius: 20px;
    cursor: pointer;
    transition: background .18s;
  }
  .toggle-sl::before {
    content: '';
    position: absolute;
    height: 16px; width: 16px;
    left: 3px; bottom: 3px;
    background: white;
    border-radius: 50%;
    transition: transform .18s;
    box-shadow: 0 1px 4px rgba(0,0,0,.18);
  }
  .toggle-sw input:checked + .toggle-sl { background: var(--accent); }
  .toggle-sw input:checked + .toggle-sl::before { transform: translateX(18px); }
  .toggle-label { font-size: .86rem; font-weight: 600; color: var(--text); }

  .member-block {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 11px;
    padding: .95rem 1.05rem;
    margin-bottom: .75rem;
  }
  .member-internal-row {
    display: flex; align-items: center; gap: .55rem; flex-wrap: wrap; margin-bottom: .65rem;
  }
  .m-badge-int {
    font-size: .63rem; font-weight: 700; letter-spacing: .06em;
    padding: .2rem .52rem; border-radius: 5px;
    background: var(--accent-light); color: var(--accent);
    border: 1px solid #bfdbfe; flex-shrink: 0;
  }
  .m-badge-ext {
    font-size: .63rem; font-weight: 700; letter-spacing: .06em;
    padding: .2rem .52rem; border-radius: 5px;
    background: var(--accent2-light); color: var(--accent2);
    border: 1px solid #ddd6fe; flex-shrink: 0;
  }
  .m-select, .m-input {
    background: var(--surface) !important;
    border: 1px solid var(--border) !important;
    border-radius: 7px !important;
    color: var(--text) !important;
    font-size: .82rem;
    padding: .4rem .68rem;
    font-family: 'DM Sans', sans-serif;
    flex: 1; min-width: 0; max-width: 215px;
    transition: border-color .13s;
    outline: none;
  }
  .m-select:focus, .m-input:focus { border-color: var(--border-focus) !important; box-shadow: 0 0 0 2px rgba(59,130,246,.1) !important; }
  .m-input::placeholder { color: var(--text-dim); }

  .ext-tbl { width: 100%; border-collapse: collapse; margin: .4rem 0 .65rem 0; }
  .ext-tbl th {
    font-size: .67rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
    color: var(--text-muted); padding: .32rem .65rem;
    border-bottom: 1px solid var(--border); text-align: left;
  }
  .ext-tbl td { padding: .35rem .65rem; }
  .ext-tbl tr:not(:last-child) td { border-bottom: 1px solid var(--border); }

  .btn-rm {
    background: transparent; border: 1px solid #fecaca; color: var(--danger);
    border-radius: 7px; padding: .3rem .55rem; font-size: .74rem; cursor: pointer;
    transition: all .13s; white-space: nowrap; font-family: 'DM Sans', sans-serif;
  }
  .btn-rm:hover { background: var(--danger-light); }

  .btn-add-ext {
    background: transparent; border: 1px dashed #ddd6fe; color: var(--accent2);
    border-radius: 7px; padding: .3rem .8rem; font-size: .78rem; cursor: pointer;
    transition: all .13s; margin-left: .4rem; font-family: 'DM Sans', sans-serif; font-weight: 600;
  }
  .btn-add-ext:hover { border-color: var(--accent2); background: var(--accent2-light); }

  .btn-add-int {
    background: transparent; border: 1px dashed #bfdbfe; color: var(--accent);
    border-radius: 9px; padding: .52rem 1rem; font-size: .83rem; cursor: pointer;
    transition: all .13s; font-family: 'DM Sans', sans-serif; font-weight: 700; width: 100%; margin-top: .25rem;
  }
  .btn-add-int:hover:not(:disabled) { background: var(--accent-light); border-color: var(--accent); }
  .btn-add-int:disabled { opacity: .35; cursor: not-allowed; }

  .btn-primary-m {
    background: var(--accent); color: #fff; border: none; border-radius: 10px;
    padding: .62rem 1.5rem; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: .86rem;
    cursor: pointer; box-shadow: 0 2px 10px rgba(37,99,235,.25);
    transition: background .14s, box-shadow .14s, transform .1s;
    display: inline-flex; align-items: center; gap: .45rem;
  }
  .btn-primary-m:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(37,99,235,.3); }
  .btn-primary-m:disabled { opacity: .6; cursor: not-allowed; }

  .btn-cancel-m {
    background: var(--surface); color: var(--text-muted); border: 1px solid var(--border);
    border-radius: 10px; padding: .62rem 1.2rem; font-family: 'DM Sans', sans-serif;
    font-weight: 600; font-size: .86rem; cursor: pointer; transition: all .13s;
  }
  .btn-cancel-m:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 13px; height: 13px;
    border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
    border-radius: 50%; animation: spin .65s linear infinite; display: inline-block;
  }

  .warn-note { font-size: .8rem; color: var(--warning); display: flex; align-items: center; gap: .4rem; margin-bottom: .65rem; }

  /* ─────────────────────── VIEW MODAL ─────────────────────── */
  .view-modal-box {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 20px;
    width: 100%;
    max-width: 700px;
    max-height: 90vh;
    box-shadow: var(--shadow-xl);
    animation: mSlideUp .2s cubic-bezier(.22,.61,.36,1);
    display: flex;
    flex-direction: column;
  }

  .view-modal-hero {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    border-radius: 20px 20px 0 0;
    padding: 1.7rem 1.9rem 1.5rem;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
  }
  .view-modal-hero::after {
  content: '🔗';
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 4rem;
  opacity: .1;
  line-height: 1;
  pointer-events: none;
}
  .view-hero-top {
    display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: .55rem;
  }
  .view-hero-name { font-size: 1.25rem; font-weight: 700; color: #fff; margin: 0 0 .28rem; letter-spacing: -.01em; }
  .view-hero-type {
    font-family: 'DM Mono', monospace;
    font-size: .68rem; font-weight: 500;
    background: rgba(255,255,255,.18); color: #fff;
    padding: .18rem .52rem; border-radius: 5px; display: inline-block;
  }
  .view-hero-status {
    display: inline-flex; align-items: center; gap: .35rem;
    padding: .3rem .85rem; border-radius: 20px; font-size: .76rem; font-weight: 700;
    flex-shrink: 0;
  }
  .view-hero-status.active { background: rgba(255,255,255,.2); color: #fff; border: 1px solid rgba(255,255,255,.35); }
  .view-hero-status.inactive { background: rgba(239,68,68,.25); color: #fecaca; border: 1px solid rgba(239,68,68,.4); }
  .view-hero-project { font-size: .84rem; color: rgba(255,255,255,.75); margin-top: .3rem; display: flex; align-items: center; gap: .4rem; }

  /* ── Scrollable view modal body ── */
  .view-modal-body {
    padding: 1.5rem 1.9rem;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .view-modal-body::-webkit-scrollbar { width: 5px; }
  .view-modal-body::-webkit-scrollbar-track { background: transparent; }
  .view-modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .view-modal-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

  .view-section { margin-bottom: 1.5rem; }
  .view-section:last-child { margin-bottom: 0; }
  .view-section-title {
    font-size: .7rem; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    color: var(--text-muted); margin-bottom: .9rem;
    display: flex; align-items: center; gap: .5rem;
  }
  .view-section-title::after { content: ''; flex: 1; height: 1px; background: var(--border); }

  .view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; }
  .view-field { display: flex; flex-direction: column; gap: .28rem; }
  .view-field.full { grid-column: 1 / -1; }
  .view-field-label { font-size: .71rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: .06em; }
  .view-field-value {
    font-size: .86rem; color: var(--text); font-weight: 500;
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 8px; padding: .52rem .8rem; word-break: break-all;
  }
  .view-field-value.mono { font-family: 'DM Mono', monospace; font-size: .79rem; color: #1e40af; }
  .view-field-value.masked { color: var(--text-muted); letter-spacing: .12em; }

  .view-auth-badge {
    display: inline-flex; align-items: center; gap: .3rem;
    font-family: 'DM Mono', monospace; font-size: .73rem; font-weight: 600;
    padding: .22rem .58rem; border-radius: 6px;
    background: var(--accent-light); color: var(--accent);
    border: 1px solid #bfdbfe;
  }

  .view-members-list { display: flex; flex-direction: column; gap: .65rem; }
  .view-member-card { background: var(--surface2); border: 1px solid var(--border); border-radius: 11px; overflow: hidden; }
  .view-member-internal {
    display: flex; align-items: center; gap: .65rem;
    padding: .7rem .95rem; background: #f0f7ff;
    border-bottom: 1px solid var(--border); flex-wrap: wrap;
  }
  .view-member-name { font-weight: 600; font-size: .86rem; color: var(--text); flex: 1; min-width: 100px; }
  .view-member-id {
    font-family: 'DM Mono', monospace; font-size: .76rem;
    color: var(--accent); background: var(--accent-light);
    padding: .18rem .5rem; border-radius: 5px; border: 1px solid #bfdbfe;
  }
  .view-member-id.empty { color: var(--text-dim); background: transparent; border-color: var(--border); font-style: italic; }

  .view-ext-list { padding: .5rem .95rem; display: flex; flex-direction: column; gap: .4rem; }
  .view-ext-row {
    display: flex; align-items: center; gap: .65rem; flex-wrap: wrap;
    padding: .4rem 0; border-bottom: 1px solid var(--border);
  }
  .view-ext-row:last-child { border-bottom: none; }
  .view-ext-name { font-size: .83rem; color: var(--text); font-weight: 500; flex: 1; min-width: 80px; }
  .view-ext-id {
    font-family: 'DM Mono', monospace; font-size: .74rem;
    color: var(--accent2); background: var(--accent2-light);
    padding: .16rem .46rem; border-radius: 5px; border: 1px solid #ddd6fe;
  }
  .view-ext-id.empty { color: var(--text-dim); background: transparent; border-color: var(--border); font-style: italic; }

  .view-no-members {
    text-align: center; color: var(--text-muted); font-size: .84rem;
    padding: 1.3rem; background: var(--surface2);
    border: 1px dashed var(--border); border-radius: 9px;
  }

  .view-modal-footer {
    padding: 1.05rem 1.9rem;
    border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    background: var(--surface2); border-radius: 0 0 20px 20px;
    flex-shrink: 0;
  }
  .view-footer-meta { font-size: .78rem; color: var(--text-muted); }
  .view-footer-meta strong { color: var(--text); }

  /* Responsive */
  @media (max-width: 768px) {
    .ei-content { padding: 1.25rem; }
    .ei-header { flex-direction: column; align-items: flex-start; gap: .75rem; }
    .ei-stats { flex-wrap: wrap; }
    .modal-backdrop { padding: .75rem; }
    .modal-box, .view-modal-box { max-height: 95vh; border-radius: 16px; }
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────────── */
const ExternalIntegration = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [projects, setProjects] = useState([]);
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); }
  }, [navigate, token]);

  const loadData = useCallback(async () => {
    try {
      const [projRes, intRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/projects/all`, { headers }),
        axios.get(`${API_BASE_URL}/api/external-integrations`, { headers }),
      ]);
      setProjects(Array.isArray(projRes.data) ? projRes.data : (projRes.data?.content || []));
      const integrationData = Array.isArray(intRes.data) ? intRes.data : [];
      setIntegrations(integrationData.filter(i => i.type === INTEGRATION_TYPE));
    } catch { toast.error("Failed to load data"); }
  }, []); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!form.projectId) { setProjectEmployees([]); return; }
    axios.get(`${API_BASE_URL}/api/manageteam/project/${form.projectId}/employees`, { headers })
      .then(res => setProjectEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => setProjectEmployees([]));
  }, [form.projectId]); // eslint-disable-line

  useEffect(() => {
    try { setMembers(JSON.parse(form.memberMappings || "[]")); }
    catch { setMembers([]); }
  }, [form.memberMappings]);

  const syncMembers = (updated) => {
    setMembers(updated);
    setForm(f => ({ ...f, memberMappings: JSON.stringify(updated) }));
  };

  const addInternalMember = () =>
    syncMembers([...members, { employeeId: "", externalAccountId: "", isInternal: true, name: "", externalMembers: [] }]);

  const updateInternalMember = (idx, field, value) => {
    const updated = members.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m, [field]: value };
      if (field === "employeeId" && value) {
        const emp = projectEmployees.find(e => String(e.id) === String(value));
        if (emp) next.name = `${emp.firstName} ${emp.lastName}`;
      }
      return next;
    });
    syncMembers(updated);
  };

  const removeInternalMember = (idx) => syncMembers(members.filter((_, i) => i !== idx));

  const addExternalMember = (parentIdx) => {
    const updated = members.map((m, i) => i !== parentIdx ? m : {
      ...m, externalMembers: [...(m.externalMembers || []), { employeeId: "", externalAccountId: "", name: "" }]
    });
    syncMembers(updated);
  };

  const updateExternalMember = (parentIdx, extIdx, field, value) => {
    const updated = members.map((m, i) => {
      if (i !== parentIdx) return m;
      const updatedExt = (m.externalMembers || []).map((em, j) => {
        if (j !== extIdx) return em;
        const next = { ...em, [field]: value };
        if (field === "employeeId" && value) {
          const emp = projectEmployees.find(e => String(e.id) === String(value));
          if (emp) next.name = `${emp.firstName} ${emp.lastName}`;
        }
        return next;
      });
      return { ...m, externalMembers: updatedExt };
    });
    syncMembers(updated);
  };

  const removeExternalMember = (parentIdx, extIdx) => {
    const updated = members.map((m, i) => i !== parentIdx ? m : {
      ...m, externalMembers: (m.externalMembers || []).filter((_, j) => j !== extIdx)
    });
    syncMembers(updated);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const openNew = () => { setEditId(null); setForm(emptyForm); setMembers([]); setShowModal(true); };
  const openEdit = (integration) => {
    setEditId(integration.id);
    setForm({
      name: integration.name || "Jira Tempo",
      type: integration.type || INTEGRATION_TYPE,
      projectId: integration.projectId || "",
      primaryServiceUrl: integration.primaryServiceUrl || "",
      primaryAuthType: integration.primaryAuthType || "BASIC",
      primaryAuthCredential: integration.primaryAuthCredential || "",
      secondaryServiceUrl: integration.secondaryServiceUrl || "",
      secondaryAuthType: integration.secondaryAuthType || "BEARER",
      secondaryAuthCredential: integration.secondaryAuthCredential || "",
      memberMappings: integration.memberMappings || "[]",
      statusSyncIntervalMinutes: integration.statusSyncIntervalMinutes ?? 15,
      fetchWorklogsDays: integration.fetchWorklogsDays ?? 30,
      active: integration.active,
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditId(null); setForm(emptyForm); setMembers([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId) { toast.error("Please select a project"); return; }
    if (!form.primaryServiceUrl) { toast.error("Primary service URL is required"); return; }
    if (!form.primaryAuthCredential) { toast.error("Primary auth credential is required"); return; }
    if (!form.secondaryServiceUrl) { toast.error("Secondary service URL is required"); return; }
    if (!form.secondaryAuthCredential) { toast.error("Secondary auth credential is required"); return; }

    setLoading(true);
    try {
      const payload = { ...form, projectId: Number(form.projectId) };
      if (editId) {
        await axios.put(`${API_BASE_URL}/api/external-integrations/${editId}`, payload, { headers });
        toast.success("Integration updated");
      } else {
        await axios.post(`${API_BASE_URL}/api/external-integrations`, payload, { headers });
        toast.success("Integration created");
      }
      closeModal();
      loadData();
    } catch { toast.error("Failed to save integration"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this integration?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/external-integrations/${id}`, { headers });
      toast.success("Deleted"); loadData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/external-integrations/${id}/toggle-active`, {}, { headers });
      loadData();
    } catch { toast.error("Failed to toggle status"); }
  };

  const projectName = (id) => projects.find(p => p.id === id)?.name || id;
  const totalActive = integrations.filter(i => i.active).length;

  return (
    <>
      <style>{styles}</style>
      <div className="fixed-top"><AdminNavbar /></div>

      <div className="ei-page">
        <div className="ei-content">

          {/* Header */}
          <div className="ei-header">
            <div className="ei-header-left">
              <div className="ei-header-icon">🔗</div>
              <div>
                <h4>Jira Tempo Integration Settings</h4>
                <p>Manage Jira &amp; Tempo connections and sync worklog mappings per project</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
              
              <button className="btn-new" onClick={openNew}>+ New Integration</button>
              <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
            </div>
          </div>

          {/* Stats */}
          <div className="ei-stats">
            <div className="stat-chip">Total <strong>{integrations.length}</strong></div>
            <div className="stat-chip">Active <strong style={{ color: "var(--success)" }}>{totalActive}</strong></div>
            <div className="stat-chip">Inactive <strong style={{ color: "var(--danger)" }}>{integrations.length - totalActive}</strong></div>
          </div>

          {/* Table or empty */}
          {integrations.length === 0 ? (
            <div className="ei-empty">
              <div className="ei-empty-icon">🔌</div>
              <h6>No integrations configured yet</h6>
              <p>Click <strong>+ New Integration</strong> to connect your first Jira Tempo workspace.</p>
            </div>
          ) : (
            <div className="ei-table-card">
              <div className="ei-table-scroll">
                <table className="ei-table">
                  <thead>
                    <tr>
                      <th>Integration</th>
                      <th>Project</th>
                      <th>Services</th>
                      <th>Auth</th>
                      <th>Members</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.map(i => {
                      let memberCount = 0;
                      try { memberCount = JSON.parse(i.memberMappings || "[]").length; } catch { }
                      return (
                        <tr key={i.id}>
                          <td>
                            <div className="td-name">{i.name}</div>
                            <div className="td-type">JIRA_TEMPO</div>
                          </td>
                          <td>
                            <div className="td-project">📁 {projectName(i.projectId)}</div>
                          </td>
                          <td>
                            <div className="td-url-wrap">
                              <div className="td-url-row">
                                <span className="url-service-label url-label-jira">JIRA</span>
                                <span className="td-url">{i.primaryServiceUrl}</span>
                              </div>
                              <div className="td-url-row">
                                <span className="url-service-label url-label-tempo">TEMPO</span>
                                <span className="td-url">{i.secondaryServiceUrl}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: ".28rem" }}>
                              <span style={{
                                fontFamily: "'DM Mono', monospace", fontSize: ".7rem", fontWeight: 600,
                                padding: ".14rem .42rem", borderRadius: "4px",
                                background: "var(--accent-light)", color: "var(--accent)",
                                display: "inline-block", width: "fit-content"
                              }}>{i.primaryAuthType}</span>
                              <span style={{
                                fontFamily: "'DM Mono', monospace", fontSize: ".7rem", fontWeight: 600,
                                padding: ".14rem .42rem", borderRadius: "4px",
                                background: "var(--accent2-light)", color: "var(--accent2)",
                                display: "inline-block", width: "fit-content"
                              }}>{i.secondaryAuthType}</span>
                            </div>
                          </td>
                          <td>
                            <div className="member-badge">
                              <div className="member-badge-dot">{memberCount}</div>
                              {memberCount === 1 ? "member" : "members"}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${i.active ? "status-active" : "status-inactive"}`}>
                              <span className="status-dot" />
                              {i.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="row-actions">
                              <button className="btn-action" onClick={() => setViewItem(i)}>👁 View</button>
                              <button className="btn-action edit" onClick={() => openEdit(i)}>✏ Edit</button>
                              <button
                                className={`btn-action ${i.active ? "toggle-on" : "toggle-off"}`}
                                onClick={() => handleToggle(i.id)}>
                                {i.active ? "⏸ Disable" : "▶ Enable"}
                              </button>
                              <button className="btn-action del" onClick={() => handleDelete(i.id)}>🗑</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="ei-table-footer">
                Showing {integrations.length} integration{integrations.length !== 1 ? "s" : ""} &nbsp;·&nbsp; {totalActive} active
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ──────────────── VIEW MODAL ──────────────── */}
      {viewItem && (() => {
        let viewMembers = [];
        try { viewMembers = JSON.parse(viewItem.memberMappings || "[]"); } catch { }
        const maskCred = (c) => c ? c.substring(0, 4) + "••••••••••••" : "—";
        return (
          <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setViewItem(null)}>
            <div className="view-modal-box" onClick={e => e.stopPropagation()}>

              <div className="view-modal-hero">
                <div className="view-hero-top">
                  <div>
                    <div className="view-hero-name">{viewItem.name}</div>
                    <div className="view-hero-type">JIRA_TEMPO</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexShrink: 0 }}>
                    <span className={`view-hero-status ${viewItem.active ? "active" : "inactive"}`}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                      {viewItem.active ? "Active" : "Inactive"}
                    </span>
                    <button className="modal-close"
                      style={{ background: "rgba(255,255,255,.15)", borderColor: "rgba(255,255,255,.3)", color: "#fff" }}
                      onClick={() => setViewItem(null)}>✕</button>
                  </div>
                </div>
                <div className="view-hero-project">
                  📁 {projectName(viewItem.projectId)}
                  {/* <span style={{ opacity: .5 }}>·</span>
                  <span>ID #{viewItem.id}</span> */}
                </div>
              </div>

              {/* Scrollable body */}
              <div className="view-modal-body">
                <div className="view-section">
                  <div className="view-section-title">Primary Service — Jira</div>
                  <div className="view-grid">
                    <div className="view-field full">
                      <div className="view-field-label">Base URL</div>
                      <div className="view-field-value mono">{viewItem.primaryServiceUrl || "—"}</div>
                    </div>
                    <div className="view-field">
                      <div className="view-field-label">Auth Type</div>
                      <div className="view-field-value">
                        <span className="view-auth-badge">{viewItem.primaryAuthType}</span>
                      </div>
                    </div>
                    <div className="view-field">
                      <div className="view-field-label">Credential</div>
                      <div className="view-field-value masked">{maskCred(viewItem.primaryAuthCredential)}</div>
                    </div>
                  </div>
                </div>

                <div className="view-section">
                  <div className="view-section-title">Secondary Service — Tempo</div>
                  <div className="view-grid">
                    <div className="view-field full">
                      <div className="view-field-label">Base URL</div>
                      <div className="view-field-value mono">{viewItem.secondaryServiceUrl || "—"}</div>
                    </div>
                    <div className="view-field">
                      <div className="view-field-label">Auth Type</div>
                      <div className="view-field-value">
                        <span className="view-auth-badge" style={{ background: "var(--accent2-light)", color: "var(--accent2)", borderColor: "#ddd6fe" }}>{viewItem.secondaryAuthType}</span>
                      </div>
                    </div>
                    <div className="view-field">
                      <div className="view-field-label">Credential</div>
                      <div className="view-field-value masked">{maskCred(viewItem.secondaryAuthCredential)}</div>
                    </div>
                  </div>
                </div>

                <div className="view-section">
                  <div className="view-section-title">Sync Settings</div>
                  <div className="view-grid">
                    <div className="view-field">
                      <div className="view-field-label">Status Sync Interval</div>
                      <div className="view-field-value">{viewItem.statusSyncIntervalMinutes ?? 15} min</div>
                    </div>
                    <div className="view-field">
                      <div className="view-field-label">Fetch Worklogs</div>
                      <div className="view-field-value">{viewItem.fetchWorklogsDays ?? 30} days back</div>
                    </div>
                  </div>
                </div>

                <div className="view-section">
                  <div className="view-section-title">Member Mappings ({viewMembers.length})</div>
                  {viewMembers.length === 0 ? (
                    <div className="view-no-members">No member mappings configured for this integration.</div>
                  ) : (
                    <div className="view-members-list">
                      {viewMembers.map((m, idx) => (
                        <div key={idx} className="view-member-card">
                          <div className="view-member-internal">
                            <span className="m-badge-int">INTERNAL</span>
                            <span className="view-member-name">{m.name || `Employee #${m.employeeId}` || "—"}</span>
                            {m.externalAccountId
                              ? <span className="view-member-id">{m.externalAccountId}</span>
                              : <span className="view-member-id empty">no account ID</span>
                            }
                          </div>
                          {(m.externalMembers || []).length > 0 && (
                            <div className="view-ext-list">
                              {(m.externalMembers || []).map((em, ei) => (
                                <div key={ei} className="view-ext-row">
                                  <span className="m-badge-ext">EXT</span>
                                  <span className="view-ext-name">{em.name || `Employee #${em.employeeId}` || "—"}</span>
                                  {em.externalAccountId
                                    ? <span className="view-ext-id">{em.externalAccountId}</span>
                                    : <span className="view-ext-id empty">uses internal ID</span>
                                  }
                                </div>
                              ))}
                            </div>
                          )}
                          {(m.externalMembers || []).length === 0 && (
                            <div style={{ padding: ".48rem .95rem", fontSize: ".76rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                              No external members under this mapping
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky footer */}
              <div className="view-modal-footer">
                <div className="view-footer-meta">
                  <strong>{viewMembers.length}</strong> member{viewMembers.length !== 1 ? "s" : ""} mapped &nbsp;·&nbsp; Integration <strong>{viewItem.active ? "Active" : "Inactive"}</strong>
                </div>
                <div style={{ display: "flex", gap: ".5rem" }}>
                  <button className="btn-primary-m" onClick={() => { setViewItem(null); openEdit(viewItem); }}>✏ Edit</button>
                  <button className="btn-cancel-m" onClick={() => setViewItem(null)}>Close</button>
                  
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ──────────────── EDIT / NEW MODAL ──────────────── */}
      {showModal && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>

            {/* Sticky header */}
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon-wrap">🔗</div>
                <div>
                  <div className="modal-title">{editId ? "Edit Integration" : "New Integration"}</div>
                  <div className="modal-subtitle">Jira Tempo · Configure service endpoints &amp; member mappings</div>
                </div>
              </div>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            {/* Scrollable body */}
            <div className="modal-body">
              <form id="int-form" onSubmit={handleSubmit}>

                <div className="form-section">
                  <div className="form-section-label">General</div>
                  <div className="form-row">
                    <div className="fg half">
                      <label className="form-label">Project <span className="req">*</span></label>
                      <select className="f-select" name="projectId" value={form.projectId} onChange={handleChange} required>
                        <option value="">— Select Project —</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="fg half">
                      <label className="form-label">Integration Name</label>
                      <input className="f-control" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Jira Tempo" />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-label">Primary Service — Jira</div>
                  <div className="form-row">
                    <div className="fg two-third">
                      <label className="form-label">Base URL <span className="req">*</span></label>
                      <input className="f-control" name="primaryServiceUrl" value={form.primaryServiceUrl} onChange={handleChange} placeholder="https://yourcompany.atlassian.net" />
                    </div>
                    <div className="fg third">
                      <label className="form-label">Auth Type</label>
                      <select className="f-select" name="primaryAuthType" value={form.primaryAuthType} onChange={handleChange}>
                        <option value="BASIC">BASIC</option>
                        <option value="BEARER">BEARER</option>
                        <option value="API_KEY">API_KEY</option>
                      </select>
                    </div>
                    <div className="fg full">
                      <label className="form-label">Credential <span className="req">*</span></label>
                      <input className="f-control" name="primaryAuthCredential" value={form.primaryAuthCredential} onChange={handleChange} placeholder="email:apiToken" />
                      <div className="form-hint">For BASIC auth: use email:apiToken format</div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-label">Secondary Service — Tempo</div>
                  <div className="form-row">
                    <div className="fg two-third">
                      <label className="form-label">Base URL <span className="req">*</span></label>
                      <input className="f-control" name="secondaryServiceUrl" value={form.secondaryServiceUrl} onChange={handleChange} placeholder="https://api.tempo.io/4" />
                    </div>
                    <div className="fg third">
                      <label className="form-label">Auth Type</label>
                      <select className="f-select" name="secondaryAuthType" value={form.secondaryAuthType} onChange={handleChange}>
                        <option value="BEARER">BEARER</option>
                        <option value="BASIC">BASIC</option>
                        <option value="API_KEY">API_KEY</option>
                      </select>
                    </div>
                    <div className="fg full">
                      <label className="form-label">Credential <span className="req">*</span></label>
                      <input className="f-control" name="secondaryAuthCredential" value={form.secondaryAuthCredential} onChange={handleChange} placeholder="Tempo API token" />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-label">Sync Settings</div>
                  <div className="form-row">
                    <div className="fg half">
                      <label className="form-label">Status Sync Interval (minutes)</label>
                      <input
                        className="f-control"
                        type="number"
                        name="statusSyncIntervalMinutes"
                        min="1"
                        max="1440"
                        value={form.statusSyncIntervalMinutes}
                        onChange={handleChange}
                        placeholder="15"
                      />
                      <div className="form-hint">How often the scheduler polls Jira for task status updates</div>
                    </div>
                    <div className="fg half">
                      <label className="form-label">Fetch Worklogs (days back)</label>
                      <input
                        className="f-control"
                        type="number"
                        name="fetchWorklogsDays"
                        min="1"
                        max="365"
                        value={form.fetchWorklogsDays}
                        onChange={handleChange}
                        placeholder="30"
                      />
                      <div className="form-hint">How many days back to look when syncing Tempo worklogs</div>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-label">Member Mappings</div>

                  {!form.projectId && (
                    <div className="warn-note">⚠ Select a project first to load employees.</div>
                  )}
                  {form.projectId && projectEmployees.length === 0 && (
                    <div className="warn-note" style={{ color: "var(--text-muted)" }}>ℹ No employees found for this project.</div>
                  )}

                  {members.map((m, idx) => (
                    <div key={idx} className="member-block">
                      <div className="member-internal-row">
                        <span className="m-badge-int">INTERNAL</span>
                        <select className="m-select" value={m.employeeId || ""}
                          onChange={e => updateInternalMember(idx, "employeeId", e.target.value)}>
                          <option value="">— Select Employee —</option>
                          {projectEmployees.map(e => (
                            <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                          ))}
                        </select>
                        <input className="m-input" value={m.externalAccountId || ""}
                          onChange={e => updateInternalMember(idx, "externalAccountId", e.target.value)}
                          placeholder="Jira accountId" />
                        <button type="button" className="btn-rm" style={{ marginLeft: "auto" }}
                          onClick={() => removeInternalMember(idx)}>✕ Remove</button>
                      </div>

                      {(m.externalMembers || []).length > 0 && (
                        <table className="ext-tbl">
                          <thead>
                            <tr>
                              <th style={{ width: 72 }}>Type</th>
                              <th>Employee</th>
                              <th>External Account ID</th>
                              <th style={{ width: 44 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(m.externalMembers || []).map((em, extIdx) => (
                              <tr key={extIdx}>
                                <td><span className="m-badge-ext">EXT</span></td>
                                <td>
                                  <select className="m-select" style={{ maxWidth: "100%" }}
                                    value={em.employeeId || ""}
                                    onChange={e => updateExternalMember(idx, extIdx, "employeeId", e.target.value)}>
                                    <option value="">— Select —</option>
                                    {projectEmployees.map(e => (
                                      <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input className="m-input" style={{ maxWidth: "100%" }}
                                    value={em.externalAccountId || ""}
                                    onChange={e => updateExternalMember(idx, extIdx, "externalAccountId", e.target.value)}
                                    placeholder="Uses internal ID if blank" />
                                </td>
                                <td>
                                  <button type="button" className="btn-rm"
                                    onClick={() => removeExternalMember(idx, extIdx)}>✕</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      <button type="button" className="btn-add-ext" onClick={() => addExternalMember(idx)}>
                        + Add External under {m.name || "this member"}
                      </button>
                    </div>
                  ))}

                  <button type="button" className="btn-add-int"
                    onClick={addInternalMember}
                    disabled={!form.projectId || projectEmployees.length === 0}>
                    + Add Internal Employee
                  </button>
                </div>

                <div className="toggle-row">
                  <label className="toggle-sw">
                    <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
                    <span className="toggle-sl" />
                  </label>
                  <span className="toggle-label">Integration Active</span>
                </div>

              </form>
            </div>

            {/* Sticky footer */}
            <div className="modal-footer">
              <button type="submit" form="int-form" className="btn-primary-m" disabled={loading}>
                {loading && <span className="spinner" />}
                {loading ? "Saving…" : editId ? "Save " : "Save "}
              </button>
              <button type="button" className="btn-cancel-m" onClick={closeModal}>Cancel</button>
              
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default ExternalIntegration;